/**
 * NAZWY TYPÓW WNIOSKÓW HR DLA CZŁOWIEKA — jedno źródło dla wszystkich ekranów.
 *
 * BYŁO ŹLE (brief właściciela 2026-09-03, obszar C): każdy ekran pisał sobie własny fallback
 * `type?.label || req.typeId`, gdzie `typeId` to KOD z bazy. Skutki były dwa:
 *
 *  1. Na liście /wnioski-hr etykieta była pusta do czasu, aż `hrTypeResolver` dociągnie typ
 *     ASYNCHRONICZNIE (useEffect PO pierwszym renderze) — więc przez chwilę na ekranie stał
 *     `REMOTE_OCCASIONAL`, a potem podmieniał się na „Praca zdalna okazjonalna". To jest
 *     dokładnie to „mija chwila, zanim się metody przetłumaczą" z briefu.
 *  2. Gdy typ zniknął z konfiguracji, fallback zostawał kodem NA STAŁE.
 *
 * NAPRAWA: serwer podaje nazwę już w PIERWSZEJ odpowiedzi listy (`typeLabelSnapshot`,
 * `typeLabel` w HrRequestDto/HrRequestListDto), więc etykieta jest dostępna od pierwszego
 * renderu i nie ma czego podmieniać. Resolver zostaje — ale tylko do IKONY i kategorii.
 *
 * KOLEJNOŚĆ (świadoma): snapshot → żywa etykieta z serwera → etykieta z resolvera → zastępcza.
 * Snapshot jest pierwszy, bo to ta sama wartość, którą drukuje PDF: ekran ma mówić to samo,
 * co pismo leżące w aktach osobowych.
 *
 * ŹRÓDŁO PRAWDY to tabela `HrRequestTypes.Label` (edytowalna w /admin/hr-config) — w tym pliku
 * NIE MA i nie wolno zakładać słownika „kod → polska nazwa": rozjechałby się z bazą przy
 * pierwszym typie dodanym przez kadry. Lustro serwera: `DogSoft.Application.Helpers.HrEtykiety`.
 */

/** Co pokazujemy, gdy typ nie ma żadnej nazwy (wiersz typu usunięty z konfiguracji). */
export const ETYKIETA_TYPU_BEZ_NAZWY = "Wniosek kadrowy (typ bez nazwy)";

const pierwszyNiepusty = (...wartosci) => {
    for (const w of wartosci) {
        if (typeof w === "string" && w.trim()) return w.trim();
    }
    return null;
};

/**
 * Nazwa typu wniosku dla człowieka.
 *
 * @param {object} req wniosek z API (`typeLabelSnapshot` / `typeLabel` / `typeCode` / `typeId`)
 * @param {object} [typ] typ dociągnięty przez hrTypeResolver (opcjonalny — bywa null przy 1. renderze)
 * @param {boolean} [krotka] preferuj `shortLabel` (wąskie kolumny tabel)
 */
export const etykietaTypuWniosku = (req, typ = null, krotka = false) => {
    const zResolvera = krotka
        ? pierwszyNiepusty(typ?.shortLabel, typ?.label)
        : pierwszyNiepusty(typ?.label, typ?.shortLabel);
    return (
        pierwszyNiepusty(req?.typeLabelSnapshot, req?.typeLabel, zResolvera) ||
        ETYKIETA_TYPU_BEZ_NAZWY
    );
};

/**
 * ŚLAD DLA ADMINISTRATORA (brief 2026-09-03, pkt e). Gdy etykieta zeszła do zastępczej,
 * zwracamy podpowiedź do `title` — administrator najeżdża myszą i wie, KTÓREGO typu brakuje
 * i gdzie to uzupełnić. Zwykły pracownik widzi wyłącznie zdanie po polsku.
 * Dla etykiety poprawnej zwraca `undefined`, czyli żadnego dymka.
 */
export const podpowiedzTypuBezNazwy = (etykieta, req) =>
    etykieta === ETYKIETA_TYPU_BEZ_NAZWY
        ? `Ten typ wniosku nie ma nazwy w konfiguracji (kod: ${req?.typeCode || req?.typeId || "?"}). Uzupełnij ją w /admin/hr-config.`
        : undefined;

/**
 * Czy tekst wygląda jak SUROWY KOD, a nie jak nazwa dla człowieka
 * (`REMOTE_OCCASIONAL`, `MANAGER_DAYOFF_ORDER`)?
 *
 * REGUŁA, NIE LISTA NAPISÓW — zapadka ma łapać także typ, którego jeszcze nie ma.
 * Świadomie nie łapie jednowyrazowych wersalików (RODO, HR, MPK to legalne polskie skróty).
 * Lustro serwera: `HrEtykiety.WygladaJakKod`.
 */
export const wygladaJakKod = (tekst) =>
    typeof tekst === "string" && /^[A-Z0-9]+(_[A-Z0-9]+)+$/.test(tekst.trim());
